import React, { useState, useEffect } from 'react';
import { Shield, HardDrive, Mail, DollarSign, Zap, Download } from 'lucide-react';
import { motion } from 'framer-motion';

// --- FeatureItem Component (No changes) ---
const FeatureItem = ({ icon: Icon, text, isHighlight = false }) => (
  <motion.li
    className={`flex items-center p-2 rounded-lg transition-all duration-200 hover:bg-plex-orange/5 ${
      isHighlight ? 'text-plex-text' : 'text-plex-text-secondary'
    }`}
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.3, delay: 0.2 }}
    whileHover={{ x: 5 }}
  >
    <div className="mr-3 p-1 rounded-full bg-plex-orange/10 flex-shrink-0">
      <Icon className="h-4 w-4 text-plex-orange" />
    </div>
    <span className="font-medium">{text}</span>
  </motion.li>
);


// --- UPDATED PricingCard Component ---
const PricingCard = ({ title, price, features, mostPopular, checkoutLink, description }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const hoverEffect = {
    scale: 1.05,
    y: -8,
    transition: { 
      duration: 0.3,
      ease: "easeOut"
    }
  };

  const baseShadow = 'shadow-lg hover:shadow-2xl';
  // Slightly enhance shadow for popular card for more depth
  const popularShadow = 'shadow-xl shadow-plex-orange/20 hover:shadow-2xl hover:shadow-plex-orange/30';

  return (
    // Outer Wrapper: Handles animation, positioning context, hover scaling
    <motion.div
      className="relative w-full h-full flex flex-col"
      variants={cardVariants}
      whileHover={hoverEffect}
    >
      {/* Badge: Positioned absolutely relative to the outer wrapper */}
      {mostPopular && (
        <>
          <div className="absolute z-10 -top-3 right-4 bg-gradient-to-r from-plex-orange to-plex-orange-light text-plex-darker px-4 py-2 rounded-full text-xs font-bold shadow-lg transform rotate-3">
            ⭐ Recommended
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-plex-orange/5 to-plex-orange-light/5 rounded-xl pointer-events-none"></div>
        </>
      )}

      {/* Inner Container: Handles background, border, padding, overflow, shadow, flex layout */}
      <div
         className={`flex flex-col h-full bg-plex-card rounded-xl overflow-hidden transition-all duration-300 ${
           mostPopular
             ? `border-2 border-plex-orange ${popularShadow} hover:border-plex-orange-light` // Use border-2 instead of ring-2
             : `border border-plex-border ${baseShadow} hover:border-plex-orange/50`
         }`}
         // Removed inline boxShadow style as Tailwind shadows are applied above
      >
        {/* Card Content Area */}
        <div className="p-8 flex-grow flex flex-col">
          <h2 className="text-2xl font-semibold text-plex-text mb-2">{title}</h2>
          <p className="text-plex-text-secondary mb-6 min-h-[3rem]">{description}</p>
          <div className="my-6">
            <p className="text-5xl font-bold text-plex-text">
              £{price}
              <span className="text-lg font-normal text-plex-text-secondary">/month</span>
            </p>
            {mostPopular}
          </div>
          <ul className="space-y-3 mb-8 flex-grow min-h-[200px] flex flex-col justify-start">
            {features.map((feature, index) => (
              <FeatureItem key={index} icon={feature.icon} text={feature.text} />
            ))}
          </ul>
        </div>

        {/* Card Footer Area */}
        <div className="p-6 bg-plex-darker mt-auto">
          <motion.a
            href={checkoutLink}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-gradient-to-r from-plex-orange to-plex-orange-light text-plex-darker py-3 px-6 rounded-lg hover:from-plex-orange-light hover:to-plex-orange hover:shadow-lg transition-all duration-300 text-center font-bold text-lg shadow-md transform hover:shadow-plex-orange/25"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
Join Now
          </motion.a>
        </div>
      </div>
    </motion.div>
  );
};


// --- ProgressBar Component ---
const ProgressBar = ({ current, goal, title, loading, error }) => {
  const progress = Math.min(100, Math.max(0, (current / goal) * 100));
  const goalReached = current >= goal;

  return (
    <motion.div
      className="w-full max-w-2xl mx-auto mb-12 p-4 bg-plex-card rounded-lg shadow-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="flex items-center justify-center mb-4">
        <DollarSign className="h-5 w-5 text-plex-orange mr-2" />
        <h3 className="text-lg font-semibold text-center text-plex-text">Community Contribution Goal</h3>
      </div>
      {loading ? (
        <div className="text-center text-plex-text-secondary py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-plex-orange"></div>
          <p className="mt-2 text-sm">Loading donation status...</p>
        </div>
      ) : (
        <>
          <div className="flex justify-between mb-1 text-sm font-medium">
            <span className="text-plex-orange">
              £{current.toFixed(2)} Raised
              {error && <span className="text-plex-orange-light ml-1">(Estimated)</span>}
            </span>
            <span className="text-plex-text-secondary">Goal: £{goal.toFixed(2)}</span>
          </div>
          <div className="h-4 w-full bg-plex-border rounded-full overflow-hidden">
            <motion.div
              className={`h-full rounded-full ${goalReached ? 'bg-green-500' : 'bg-gradient-to-r from-plex-orange to-plex-orange-light'}`}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
            >
              <span className="sr-only">{progress.toFixed(0)}% Complete</span>
            </motion.div>
          </div>
          {goalReached && (
            <motion.p 
              className="text-center text-green-400 mt-2 text-sm font-semibold flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 2 }}
            >
              ✨ Goal Reached! Thank you for your support! ✨
            </motion.p>
          )}
          {error && (
            <p className="text-center text-plex-orange-light mt-2 text-xs">{error}</p>
          )}
        </>
      )}
    </motion.div>
  );
};

// --- Main App Component (No changes) ---
const App = () => {
  const [donationData, setDonationData] = useState({ current: 2, loading: true, error: null });
  const goal = 21.47;

  useEffect(() => {
    const fetchDonationStatus = async () => {
      try {
        // In development, the API route doesn't exist, so we'll use a fallback
        // In production on Vercel, this will work correctly
        const isDevelopment = process.env.NODE_ENV === 'development';
        
        if (isDevelopment) {
          // For now, use the fallback value in development
          console.log('Development mode: using fallback donation amount');
          setDonationData({ current: 2, loading: false, error: 'Using development fallback' });
          return;
        }

        const response = await fetch('/api/donation-status');
        if (!response.ok) {
          throw new Error('Failed to fetch donation status');
        }
        const data = await response.json();
        setDonationData({ current: data.total, loading: false, error: null });
      } catch (error) {
        console.error('Error fetching donation status:', error);
        setDonationData({ current: 2, loading: false, error: 'Failed to load live data' });
      }
    };

    fetchDonationStatus();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-plex-dark via-plex-darker to-plex-dark text-plex-text p-8 font-sans">
      <div className="max-w-7xl mx-auto">
         {/* --- Header Section --- */}
         <motion.div
           className="text-center mb-16"
           initial={{ opacity: 0, y: -30 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.6 }}
         >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-plex-orange to-plex-orange-light">
            PlexFlex Donation Tiers
          </h1>
          <p className="text-xl md:text-2xl text-plex-text mb-4 max-w-4xl mx-auto font-medium">
            Enjoy seamless Plex streaming hosted by us, enhanced with valuable extras
          </p>
          <p className="text-base md:text-lg text-plex-text-secondary max-w-3xl mx-auto mb-8">
            Access unlimited 4K content, secure VPN, and premium cloud storage. Choose the plan that fits your needs.
          </p>
        </motion.div>

        {/* --- Progress Bar Section --- */}
        <ProgressBar
          current={donationData.current}
          goal={goal}
          title="Monthly Server Costs Contribution"
          loading={donationData.loading}
          error={donationData.error}
        />

      {/* --- Pricing Cards Section --- */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-7xl mx-auto px-4" // Grid layout for consistent sizing
        initial="hidden"
        animate="visible"
        variants={{
          visible: { transition: { staggerChildren: 0.2 } }
        }}
      >
        <PricingCard
          title="Supporter"
          price="2"
          description="Help with basic server costs and get VPN access."
          features={[
            { icon: Shield, text: "FlexVPN Community Access" }
          ]}
          checkoutLink="https://buy.stripe.com/fZe4i4fM2cQ5gY84go"
        />
        <PricingCard
          title="Member"
          price="3"
          description="Essential tier supporting server costs with secure storage."
          features={[
            { icon: Shield, text: "FlexVPN Community Access" },
            { icon: HardDrive, text: "50GB Encrypted Cloud Storage" }
          ]}
          checkoutLink="https://buy.stripe.com/dR615SarI17n0ZabIP"
        />
        <PricingCard
          title="Advocate"
          price="5"
          description="Enhanced contribution with extra community benefits."
          features={[
            { icon: Shield, text: "FlexVPN Community Access" },
            { icon: HardDrive, text: "100GB Encrypted Cloud Storage" },
            { icon: Mail, text: "Custom @tinymail.uk email alias" },
            { icon: Download, text: "Offline Downloads for Mobile" },
          ]}
          mostPopular={true}
          checkoutLink="https://buy.stripe.com/14kdSEfM2eYd23eaEK"
        />
        <PricingCard
          title="Champion"
          price="7"
          description="Top-tier contribution supporting 4K community content."
          features={[
            { icon: Shield, text: "FlexVPN Community Access" },
            { icon: HardDrive, text: "150GB Encrypted Cloud Storage" },
            { icon: Mail, text: "Custom @tinymail.uk email alias" },
            { icon: Download, text: "Offline Downloads for Mobile" },
            { icon: Zap, text: "4K Content Request Privileges"}
          ]}
          checkoutLink="https://buy.stripe.com/9AQ7ugarI6rHeQ0dQZ"
        />
          
        </motion.div>


        {/* --- Footer --- */}
        <motion.div
          className="text-center mt-16 text-plex-text-muted text-sm"
           initial={{ opacity: 0 }}
           animate={{ opacity: 1 }}
           transition={{ duration: 0.5, delay: 1 }}
          >
            <div className="bg-plex-card rounded-lg p-6 max-w-2xl mx-auto border border-plex-border">
              <p className="flex items-center justify-center mb-2">
                Monthly contributions go directly towards server costs. 
                <DollarSign className="inline h-4 w-4 ml-1 text-plex-orange" /> 
              </p>
              <p className="mb-3">Questions about our community? 
                <a href="/contact" className="text-plex-orange hover:text-plex-orange-light transition-colors duration-200 font-medium"> Get in Touch</a>
              </p>
              <p className="text-xs opacity-75">Our community server runs on
                <a href="https://www.plex.tv/" target="_blank" rel="noopener noreferrer" className="text-plex-text-secondary hover:text-plex-text underline transition-colors duration-200"> Plex Media Server</a>.
              </p>
            </div>
            <div className="mt-8 flex justify-center space-x-4 text-xs">
              <span className="flex items-center">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                All systems operational
              </span>
              <span>•</span>
              <span>Last updated: {new Date().toLocaleDateString()}</span>
            </div>
        </motion.div>

      </div>
    </div>
  );
};

export default App;
